import { useNavigate, useLocation } from 'react-router-dom';
import {
	useState,
	useMemo,
	useEffect,
	useRef,
	useCallback,
	type ComponentType,
} from 'react';
import {
	ChevronLeft,
	Loader2,
	ClipboardList,
	ArrowRight,
	Clock,
	ShieldCheck,
	Trophy,
} from 'lucide-react';
import {
	DynamicIcon as DynamicIconRaw,
	type IconName,
} from 'lucide-react/dynamic';
import { Helmet } from 'react-helmet-async';
import {
	useGetAvailableForms,
	useGetMyFormAnswersAnalytics,
} from '@/services/queries/form';
import { useSubmitFormAnswer } from '@/services/mutations/form';
import { Button } from '@/components/ui';
import { useModal } from '@/hooks';
import { FormContinuationMock } from './FormContinuationMock';
import { FormWellbeingPortraitMock } from './FormWellbeingPortraitMock';
import { FormEnergyDimensionsMock } from './FormEnergyDimensionsMock';
import { FormWellbeingSavedMock } from './FormWellbeingSavedMock';
import {
	getContrastTextClassForColorInput,
	hexPattern,
	normalizeHex,
	resolveColorInputToHex,
} from '@/utils/formQuestionColor';
import { filterAnswerableForms, resolvePostFormDestination } from '@/utils';

type UserFormularioFlowStage =
	| 'form'
	| 'betweenForms'
	| 'continuation'
	| 'portrait'
	| 'energyDimensions'
	| 'wellbeingSaved'
	| 'done';

const hexToRgba = (hex: string, alpha: number) => {
	if (!hexPattern.test(hex)) return undefined;
	const n = normalizeHex(hex).replace('#', '');
	const r = parseInt(n.slice(0, 2), 16);
	const g = parseInt(n.slice(2, 4), 16);
	const b = parseInt(n.slice(4, 6), 16);
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const DynamicIcon = DynamicIconRaw as ComponentType<{
	name: IconName;
	size?: number | string;
	className?: string;
}>;

const UserFormulario = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const postFormDestination = useMemo(
		() => resolvePostFormDestination(location.state),
		[location.state],
	);

	const navigateAfterForm = useCallback(() => {
		navigate(postFormDestination, { replace: true });
	}, [navigate, postFormDestination]);

	const {
		data: availableData,
		isLoading: isLoadingAvailable,
		refetch: refetchAvailable,
	} = useGetAvailableForms(
		{ page: 1, limit: 100 },
		{ refetchOnWindowFocus: false },
	);

	const forms = availableData?.data ?? [];
	const sortedForms = useMemo(
		() =>
			filterAnswerableForms(forms).sort((a, b) => {
				if (a.type === b.type) return 0;
				return a.type === 'ONBOARDING' ? -1 : 1;
			}),
		[forms],
	);

	const currentForm = sortedForms[0];

	const { mutateAsync: submitAnswer, isPending: isSubmitting } =
		useSubmitFormAnswer(() => {});

	const questions = useMemo(
		() =>
			(currentForm?.form_question ?? []).sort(
				(a, b) => a.number - b.number,
			),
		[currentForm?.form_question],
	);

	const [currentStep, setCurrentStep] = useState(0);
	const [answers, setAnswers] = useState<Record<string, string | null>>({});
	const [flowStage, setFlowStage] = useState<UserFormularioFlowStage>('form');
	const [completedFormTitle, setCompletedFormTitle] = useState<string | null>(
		null,
	);
	const [isResolvingFinalStage, setIsResolvingFinalStage] = useState(false);
	/** Enquanto re-sincroniza a lista após envio: evita ir para /home antes de `setFlowStage('continuation')`. */
	const [isSyncingFormListAfterSubmit, setIsSyncingFormListAfterSubmit] =
		useState(false);
	const [hasCbiInFlow, setHasCbiInFlow] = useState(false);
	const [hoveredKey, setHoveredKey] = useState<string | null>(null);
	/** Só exibe a tela de intro antes do primeiro formulário; em sequência de 2+ forms não mostra de novo */
	const [introDismissed, setIntroDismissed] = useState(false);
	const betweenFormsTrophyRef = useRef<HTMLDivElement>(null);
	const nextButtonRef = useRef<HTMLDivElement>(null);
	const prevCanAdvanceRef = useRef(false);
	const stageRootRef = useRef<HTMLDivElement>(null);
	const { data: myAnalytics, isLoading: isLoadingMyAnalytics } =
		useGetMyFormAnswersAnalytics({
			enabled: flowStage === 'portrait',
		});

	useEffect(() => {
		if (sortedForms.some((form) => form.type === 'CBI')) {
			setHasCbiInFlow(true);
		}
	}, [sortedForms]);

	const findScrollParent = (startEl: HTMLElement | null) => {
		if (typeof window === 'undefined') return null;
		let node: HTMLElement | null = startEl?.parentElement ?? null;
		while (node && node !== document.body) {
			const style = window.getComputedStyle(node);
			const overflowY = style.overflowY;
			const canScrollY =
				(overflowY === 'auto' || overflowY === 'scroll') &&
				node.scrollHeight > node.clientHeight;
			if (canScrollY) return node;
			node = node.parentElement;
		}
		return (document.scrollingElement as HTMLElement | null) ?? null;
	};

	const scrollToTop = (behavior: ScrollBehavior = 'smooth') => {
		if (typeof window === 'undefined') return;
		const target = findScrollParent(stageRootRef.current);
		target?.scrollTo({ top: 0, behavior });
	};

	const totalSteps = questions.length;
	const currentQuestion = questions[currentStep];
	const rawQuestionColor =
		currentQuestion?.color && currentQuestion.color.trim().length > 0
			? currentQuestion.color.trim()
			: '#e5e7eb';
	const resolvedQuestionColor =
		resolveColorInputToHex(rawQuestionColor) ?? '#e5e7eb';
	const PROGRESS_COLOR_CLASS = 'bg-teal-500';
	const questionNumber = currentQuestion?.number ?? 1;
	const questionTextColorClass = getContrastTextClassForColorInput(
		rawQuestionColor,
		'#e5e7eb',
	);

	const [hasSavedAnswers, setHasSavedAnswers] = useState(false);

	useEffect(() => {
		if (totalSteps === 0) return;
		const raw = localStorage.getItem('plumaa_form_answers');
		setHasSavedAnswers(Boolean(raw));
	}, [totalSteps]);

	const { openModal, closeModal } = useModal();
	const [hasOpenedModal, setHasOpenedModal] = useState(false);

	useEffect(() => {
		if (!hasSavedAnswers) return;
		if (hasOpenedModal) return;

		openModal({
			title: 'Continuar de onde parou?',
			content: (
				<div className="space-y-4 text-left">
					<p className="text-sm text-stone-600">
						Você tem respostas salvas deste formulário. Deseja
						continuar de onde parou?
					</p>
					<div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
						<Button
							type="button"
							variant="outline"
							text="Começar do zero"
							className="justify-center rounded-xl px-4 py-2.5 font-semibold"
							onClick={handleStartFromZero}
						/>
						<Button
							type="button"
							variant="contained"
							text="Continuar"
							className="justify-center rounded-xl bg-stone-900 px-4 py-2.5 font-semibold text-white hover:bg-stone-800"
							onClick={handleResumeSavedAnswers}
						/>
					</div>
				</div>
			),
		});
		setHasOpenedModal(true);
	}, [hasSavedAnswers, hasOpenedModal, openModal]);

	const getStepFromSavedAnswers = (saved: Record<string, string | null>) => {
		if (totalSteps <= 1) return 0;

		let answeredCount = 0;

		for (const question of questions) {
			const key = String(question.number);
			if (!Object.prototype.hasOwnProperty.call(saved, key)) break;
			const value = saved[key];

			if (value === null) {
				answeredCount += 1;
				continue;
			}

			if (question.type === 'FREE_TEXT') {
				if ((value ?? '').trim().length > 0) answeredCount += 1;
				else break;
				continue;
			}

			if ((value ?? '').length > 0) {
				answeredCount += 1;
				continue;
			}
			break;
		}

		if (answeredCount >= totalSteps) return totalSteps - 1;
		return answeredCount;
	};

	const handleResumeSavedAnswers = () => {
		const raw = localStorage.getItem('plumaa_form_answers');
		if (!raw) {
			closeModal();
			return;
		}

		try {
			const parsed = JSON.parse(raw);
			if (!parsed || typeof parsed !== 'object') {
				throw new Error('invalid-saved-answers');
			}
			const safeParsed = parsed as Record<string, string | null>;
			setAnswers(safeParsed);
			setCurrentStep(getStepFromSavedAnswers(safeParsed));
			setIntroDismissed(true);
		} catch {
			localStorage.removeItem('plumaa_form_answers');
			setAnswers({});
			setCurrentStep(0);
			setIntroDismissed(true);
		} finally {
			closeModal();
		}
	};

	const handleStartFromZero = () => {
		localStorage.removeItem('plumaa_form_answers');
		setAnswers({});
		setCurrentStep(0);
		setIntroDismissed(true);
		closeModal();
	};

	// enquanto responde, progresso = passos já concluídos (não o passo atual)
	const progressPercent = useMemo(
		() => (totalSteps ? Math.round((currentStep / totalSteps) * 100) : 0),
		[currentStep, totalSteps],
	);

	const setAnswer = (key: string, value: string | null) => {
		setAnswers((prev) => ({ ...prev, [key]: value }));
	};

	const canAdvance = useMemo(() => {
		if (!currentQuestion) return false;
		const v = answers[String(currentQuestion.number)];
		if (currentQuestion.type === 'FREE_TEXT')
			return (v ?? '').trim().length > 0;
		if (currentQuestion.type === 'SINGLE_CHOICE')
			return (v ?? '').length > 0;
		if (currentQuestion.type === 'MULTIPLE_CHOICE') {
			const validKeys = new Set(
				(currentQuestion.options ?? []).map((o) => o.key),
			);
			const chars = (v ?? '').split('');
			const selected = chars.filter((c) => c.trim() !== '');
			return (
				selected.length > 0 && selected.every((c) => validKeys.has(c))
			);
		}
		return false;
	}, [currentQuestion, answers]);

	useEffect(() => {
		if (canAdvance && !prevCanAdvanceRef.current) {
			nextButtonRef.current?.scrollIntoView({
				behavior: 'smooth',
				block: 'end',
			});
		}
		prevCanAdvanceRef.current = canAdvance;
	}, [canAdvance]);

	useEffect(() => {
		scrollToTop('smooth');
	}, [currentStep]);

	useEffect(() => {
		scrollToTop('auto');
	}, [flowStage]);

	useEffect(() => {
		if (flowStage !== 'betweenForms') return;
		const trophyEl = betweenFormsTrophyRef.current;
		if (!trophyEl) return;

		// Entrada estilo "pop/bounce" inspirada em feedback positivo de games/apps.
		trophyEl.animate(
			[
				{
					opacity: 0,
					transform: 'translateY(16px) scale(0.68) rotate(-10deg)',
				},
				{
					opacity: 1,
					transform: 'translateY(-5px) scale(1.1) rotate(4deg)',
					offset: 0.45,
				},
				{
					opacity: 1,
					transform: 'translateY(1px) scale(0.97) rotate(-2deg)',
					offset: 0.78,
				},
				{
					opacity: 1,
					transform: 'translateY(0px) scale(1) rotate(0deg)',
				},
			],
			{
				delay: 260,
				duration: 1700,
				fill: 'both',
				easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
			},
		);
	}, [flowStage]);

	const buildAnswerPayload = (
		sourceAnswers: Record<string, string | null>,
	) => {
		const answerPayload: Record<string, string | null> = {};
		questions.forEach((q) => {
			const v = sourceAnswers[String(q.number)];
			answerPayload[String(q.number)] = v != null && v !== '' ? v : null;
		});
		return answerPayload;
	};

	const handleNext = ({
		skipCurrent = false,
	}: { skipCurrent?: boolean } = {}) => {
		if (!currentQuestion) return;
		const currentQuestionKey = String(currentQuestion.number);
		const nextAnswers = skipCurrent
			? { ...answers, [currentQuestionKey]: null }
			: answers;

		if (skipCurrent) {
			setAnswers(nextAnswers);
		}

		if (currentStep < totalSteps - 1) {
			localStorage.setItem(
				'plumaa_form_answers',
				JSON.stringify(nextAnswers),
			);
			setCurrentStep((s) => s + 1);
		} else if (currentForm?.id) {
			setIsResolvingFinalStage(true);
			const answerPayload = buildAnswerPayload(nextAnswers);

			localStorage.removeItem('plumaa_form_answers');
			const applicationPeriodId =
				currentForm.application_mode === 'PER_APPLICATION_PERIOD'
					? currentForm.active_application_period_id ?? null
					: null;

			if (
				currentForm.application_mode === 'PER_APPLICATION_PERIOD' &&
				!applicationPeriodId
			) {
				refetchAvailable()
					.catch(() => {})
					.finally(() => {
						setIsResolvingFinalStage(false);
					});
				return;
			}
			const submission = submitAnswer({
				formId: currentForm.id,
				body: {
					answer: answerPayload,
					...(applicationPeriodId
						? { application_period_id: applicationPeriodId }
						: {}),
				},
			});
			// Importante: não acoplar ao `await refetchAvailable()` — se o refetch travar,
			// `isResolvingFinalStage` nunca zerava e o efeito de redirect para /home ficava bloqueado para sempre.
			submission.finally(() => {
				setIsResolvingFinalStage(false);
			});
			const REFETCH_AFTER_SUBMIT_MS = 25_000;
			submission
				.then(async () => {
					setIsSyncingFormListAfterSubmit(true);
					try {
						const result = await Promise.race([
							refetchAvailable(),
							new Promise<
								Awaited<ReturnType<typeof refetchAvailable>>
							>((_, reject) =>
								setTimeout(
									() => reject(new Error('refetch-timeout')),
									REFETCH_AFTER_SUBMIT_MS,
								),
							),
						]);
						const remaining = filterAnswerableForms(
							result.data?.data ?? [],
						);
						const shouldShowWellbeingFlow =
							hasCbiInFlow || currentForm.type === 'CBI';

						if (remaining.length === 0) {
							if (shouldShowWellbeingFlow) {
								// Último formulário e havia CBI no fluxo: segue para cálculo e retrato.
								setFlowStage('continuation');
							} else {
								// Sem CBI no fluxo: não exibe telas de cálculo/retrato.
								navigateAfterForm();
							}
						} else {
							// Ainda tem formulário pendente: exibe tela de transição antes do próximo.
							setCompletedFormTitle(
								currentForm.title ?? 'Formulário',
							);
							setFlowStage('betweenForms');
						}
					} catch {
						// Envio já deu certo; em caso de timeout/erro no refetch, só segue para cálculo se houver CBI.
						if (hasCbiInFlow || currentForm.type === 'CBI') {
							setFlowStage('continuation');
						} else {
							navigateAfterForm();
						}
					} finally {
						setIsSyncingFormListAfterSubmit(false);
					}
				})
				.catch(() => {});
		}
	};

	const handleBack = () => {
		if (currentStep > 0) setCurrentStep((s) => s - 1);
	};

	const handleSkip = () => {
		if (isSubmitting) return;
		handleNext({ skipCurrent: true });
	};

	const handleContinueToNextForm = () => {
		setAnswers({});
		setCurrentStep(0);
		setFlowStage('form');
	};

	const handleSingleChoice = (key: string) => {
		if (!currentQuestion) return;
		setAnswer(String(currentQuestion.number), key);
	};

	const handleMultipleToggle = (key: string) => {
		if (!currentQuestion) return;
		const current = (answers[String(currentQuestion.number)] ?? '')
			.split('')
			.map((s) => s.trim())
			.filter(Boolean);
		const next = current.includes(key)
			? current.filter((k) => k !== key)
			: [...current, key];
		// Backend espera MULTIPLE_CHOICE como string de chaves concatenadas (ex: "ABE")
		setAnswer(String(currentQuestion.number), next.sort().join(''));
	};

	useEffect(() => {
		if (flowStage !== 'form') return;
		if (!introDismissed) return;
		if (!currentQuestion) return;
		if (currentQuestion.type === 'FREE_TEXT') return;
		if (isSubmitting) return;

		const options = currentQuestion.options ?? [];
		if (options.length === 0) return;

		const onKeyDown = (event: KeyboardEvent) => {
			if (event.defaultPrevented) return;
			if (event.ctrlKey || event.metaKey || event.altKey) return;

			const target = event.target as HTMLElement | null;
			if (target) {
				const tagName = target.tagName;
				if (
					tagName === 'INPUT' ||
					tagName === 'TEXTAREA' ||
					tagName === 'SELECT' ||
					target.isContentEditable
				) {
					return;
				}
			}

			const parsedNumber = Number.parseInt(event.key, 10);
			if (
				!Number.isInteger(parsedNumber) ||
				parsedNumber < 1 ||
				parsedNumber > 9
			) {
				return;
			}

			const selectedOption = options[parsedNumber - 1];
			if (!selectedOption) return;

			event.preventDefault();

			if (currentQuestion.type === 'SINGLE_CHOICE') {
				handleSingleChoice(selectedOption.key);
				return;
			}

			if (currentQuestion.type === 'MULTIPLE_CHOICE') {
				handleMultipleToggle(selectedOption.key);
			}
		};

		window.addEventListener('keydown', onKeyDown);
		return () => window.removeEventListener('keydown', onKeyDown);
	}, [flowStage, introDismissed, currentQuestion, isSubmitting, answers]);

	useEffect(() => {
		if (flowStage !== 'form') return;
		if (!introDismissed) return;
		if (!currentQuestion) return;

		const onEnterKeyDown = (event: KeyboardEvent) => {
			const isEnterKey =
				event.key === 'Enter' ||
				event.code === 'Enter' ||
				event.code === 'NumpadEnter';
			if (!isEnterKey) return;
			if (!canAdvance || isSubmitting) return;

			event.preventDefault();
			handleNext();
		};

		window.addEventListener('keydown', onEnterKeyDown);
		return () => window.removeEventListener('keydown', onEnterKeyDown);
	}, [
		flowStage,
		introDismissed,
		currentQuestion,
		canAdvance,
		isSubmitting,
		answers,
	]);

	useEffect(() => {
		if (flowStage !== 'betweenForms') return;

		const onEnterKeyDown = (event: KeyboardEvent) => {
			const isEnterKey =
				event.key === 'Enter' ||
				event.code === 'Enter' ||
				event.code === 'NumpadEnter';
			if (!isEnterKey) return;

			event.preventDefault();
			handleContinueToNextForm();
		};

		window.addEventListener('keydown', onEnterKeyDown);
		return () => window.removeEventListener('keydown', onEnterKeyDown);
	}, [flowStage]);

	const isLoading = isLoadingAvailable;

	useEffect(() => {
		if (flowStage !== 'form') return;
		if (isResolvingFinalStage) return;
		if (isSyncingFormListAfterSubmit) return;
		if (!isLoading && (!currentForm || totalSteps === 0)) {
			navigateAfterForm();
		}
	}, [
		flowStage,
		isResolvingFinalStage,
		isSyncingFormListAfterSubmit,
		isLoading,
		currentForm,
		totalSteps,
		navigateAfterForm,
	]);

	// Total de perguntas em TODOS os formulários (para estimativa: 15s por pergunta) — hooks sempre na mesma ordem
	const totalQuestionsAllForms = useMemo(
		() =>
			sortedForms.reduce(
				(acc, f) => acc + (f.form_question?.length ?? 0),
				0,
			),
		[sortedForms],
	);
	const totalSecondsEstimate = totalQuestionsAllForms * 15;
	const estimatedTimeLabel =
		totalSecondsEstimate < 60
			? `${totalSecondsEstimate} s`
			: `${Math.floor(totalSecondsEstimate / 60)} min${
					totalSecondsEstimate % 60 > 0
						? ` ${totalSecondsEstimate % 60} s`
						: ''
			  }`;
	const handleStartForm = () => setIntroDismissed(true);
	useEffect(() => {
		if (flowStage !== 'form') return;
		if (introDismissed) return;

		const onKeyDown = (event: KeyboardEvent) => {
			const isEnterKey =
				event.key === 'Enter' ||
				event.code === 'Enter' ||
				event.code === 'NumpadEnter';
			if (!isEnterKey) return;

			event.preventDefault();
			handleStartForm();
		};

		window.addEventListener('keydown', onKeyDown);
		return () => window.removeEventListener('keydown', onKeyDown);
	}, [flowStage, introDismissed]);

	if (flowStage === 'continuation') {
		return (
			<div ref={stageRootRef}>
				<FormContinuationMock
					onFlowFinish={() => setFlowStage('portrait')}
				/>
			</div>
		);
	}

	if (flowStage === 'portrait') {
		const cbiItem = myAnalytics?.data?.CBI;
		const petItem = myAnalytics?.data?.PET;
		const portraitAnalysis =
			cbiItem?.analysis_data ?? petItem?.analysis_data;
		const totalScore = portraitAnalysis?.total_score;
		const firstImpactFlag = portraitAnalysis?.categories?.find(
			(category) => category.is_positive_impact !== null,
		)?.is_positive_impact;

		return (
			<div ref={stageRootRef}>
				<FormWellbeingPortraitMock
					isLoading={isLoadingMyAnalytics}
					data={
						typeof totalScore === 'number'
							? {
									scoreValue: totalScore,
									isPositiveImpact: firstImpactFlag,
							  }
							: undefined
					}
					onContinue={() => setFlowStage('energyDimensions')}
				/>
			</div>
		);
	}

	if (flowStage === 'energyDimensions') {
		return (
			<div
				ref={stageRootRef}
				className="flex min-h-full w-full min-w-0 flex-col"
			>
				<FormEnergyDimensionsMock
					onDecline={() => setFlowStage('wellbeingSaved')}
					declineRoute={postFormDestination}
				/>
			</div>
		);
	}

	if (flowStage === 'wellbeingSaved') {
		return (
			<div ref={stageRootRef}>
				<FormWellbeingSavedMock completionRoute={postFormDestination} />
			</div>
		);
	}

	if (flowStage === 'done') {
		return (
			<div
				ref={stageRootRef}
				className="mx-auto flex min-h-full w-full max-w-5xl flex-col items-center justify-center px-6 py-20"
			>
				<Loader2 className="h-10 w-10 animate-spin text-zinc-400" />
				<p className="mt-4 text-sm text-zinc-500">
					Preparando próxima etapa…
				</p>
			</div>
		);
	}

	if (flowStage === 'betweenForms') {
		return (
			<div
				ref={stageRootRef}
				className="mx-auto flex min-h-full w-full max-w-5xl flex-col items-center justify-center px-6 py-16"
			>
				<div className="w-full max-w-xl rounded-3xl border border-brand-orange/20 bg-white p-7 text-center shadow-sm md:p-10">
					<div
						ref={betweenFormsTrophyRef}
						className="mx-auto flex h-28 w-28 items-center justify-center rounded-3xl text-brand-orange"
					>
						<Trophy className="h-14 w-14" strokeWidth={2.1} />
					</div>
					<h2 className="text-4xl font-black italic tracking-tight text-zinc-900">
						Sensacional!
					</h2>
					<p className="mx-auto mt-5 max-w-lg text-lg leading-relaxed text-zinc-600">
						Você concluiu o formulário{' '}
						<span className="font-bold text-brand-orange">
							"{completedFormTitle}"
						</span>{' '}
						com sucesso.
					</p>
					<div className="mt-8">
						<Button
							onClick={handleContinueToNextForm}
							text="Continuar para o próximo"
							endIcon={<ArrowRight className="h-5 w-5" />}
							className="w-full justify-center rounded-xl bg-brand-orange px-6 py-3.5 font-semibold text-white hover:bg-brand-orange/90"
						/>
					</div>
					<p className="mt-5 text-sm font-semibold text-zinc-400">
						Seu progresso segue salvo automaticamente.
					</p>
				</div>
			</div>
		);
	}

	if (isLoading) {
		return (
			<div
				ref={stageRootRef}
				className="mx-auto flex min-h-full w-full max-w-5xl flex-col items-center justify-center px-6 py-20"
			>
				<Loader2 className="h-10 w-10 animate-spin text-zinc-400" />
				<p className="mt-4 text-sm text-zinc-500">
					Carregando formulários…
				</p>
			</div>
		);
	}

	if (!currentForm || totalSteps === 0) {
		return (
			<div
				ref={stageRootRef}
				className="mx-auto flex min-h-full w-full max-w-5xl flex-col items-center justify-center px-6 py-20"
			>
				<Loader2 className="h-10 w-10 animate-spin text-zinc-400" />
				<p className="mt-4 text-sm text-zinc-500">Redirecionando…</p>
			</div>
		);
	}

	if (!introDismissed) {
		return (
			<div className="flex min-h-full w-full flex-col md:flex-row">
				<Helmet>
					<title>Antes de começar | Plumaa</title>
					<meta
						name="description"
						content="Sua voz transforma o amanhã. Estamos aqui para ouvir você."
					/>
				</Helmet>

				{/* Painel esquerdo: referência estrutural — hero + card de lista + métricas */}
				<div className="flex flex-1 flex-col justify-between bg-brand-orange/90 px-6 py-8 md:min-h-0 md:px-10 md:py-12 lg:px-14">
					<div className="flex flex-1 flex-col">
						<h1 className="max-w-xl text-3xl font-black leading-[1.1] tracking-tight text-white md:text-4xl lg:text-[2.75rem]">
							Sua voz transforma o{' '}
							<span className="italic text-white">amanhã.</span>
						</h1>
						<p className="mt-5 max-w-lg text-base leading-relaxed text-white/90 md:text-lg">
							Estamos aqui para ouvir você. Suas respostas são o
							primeiro passo para construirmos um ambiente mais
							acolhedor e eficiente.
						</p>

						{sortedForms.length > 0 && (
							<div className="mt-8 rounded-2xl border border-white/60 bg-white/95 p-4 shadow-lg md:p-5">
								<p className="text-[10px] font-bold uppercase tracking-[0.22em] text-brand-orange/90">
									Formulários disponíveis
								</p>
								<ul className="mt-4 space-y-1">
									{sortedForms.map((form, index) => {
										const label =
											form.title ||
											'Formulário sem título';
										const num = String(index + 1).padStart(
											2,
											'0',
										);
										const isActive = index === 0;
										return (
											<li
												key={form.id}
												className={`flex items-center gap-3 rounded-xl px-2 py-2 ${
													isActive
														? 'border border-brand-orange bg-white px-3 py-3 shadow-sm'
														: ''
												}`}
											>
												<span
													className={`flex h-9 min-w-9 shrink-0 items-center justify-center rounded-full text-xs font-black tabular-nums ${
														isActive
															? 'bg-brand-orange/10 text-brand-orange'
															: 'bg-zinc-100 text-zinc-600'
													}`}
												>
													{num}
												</span>
												<span
													className={`min-w-0 flex-1 text-sm font-semibold leading-snug md:text-base ${
														isActive
															? 'text-zinc-900'
															: 'text-zinc-700'
													}`}
												>
													{label}
												</span>
												{isActive && (
													<span className="inline-flex size-7 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange">
														<ArrowRight className="size-3.5" />
													</span>
												)}
											</li>
										);
									})}
								</ul>
							</div>
						)}
					</div>

					<div className="mt-10 flex flex-wrap items-end gap-8 border-t border-white/15 pt-8 md:mt-12 md:gap-14 md:pt-10">
						<div className="flex items-start gap-3">
							<span className="mt-0.5 inline-flex rounded-lg bg-white/15 p-2 text-white">
								<Clock className="h-5 w-5" strokeWidth={2} />
							</span>
							<div>
								<p className="text-2xl font-black tabular-nums text-white md:text-3xl">
									~{estimatedTimeLabel}
								</p>
								<p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-white/75">
									Tempo estimado
								</p>
							</div>
						</div>
						<div className="flex items-start gap-3">
							<span className="mt-0.5 inline-flex rounded-lg bg-white/15 p-2 text-white">
								<ShieldCheck
									className="h-5 w-5"
									strokeWidth={2}
								/>
							</span>
							<div>
								<p className="text-2xl font-black text-white md:text-3xl">
									100%
								</p>
								<p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-white/75">
									Confidencial
								</p>
							</div>
						</div>
					</div>
				</div>

				{/* Painel direito: branco */}
				<div className="flex flex-1 flex-col justify-center bg-white px-6 py-10 md:px-12 md:py-14 lg:px-16">
					<div className="mx-auto w-full max-w-3xl">
						<div className="flex h-14 w-14 items-center justify-center rounded-xl bg-brand-orange/90 text-white shadow-sm ring-1 ring-brand-orange/20">
							<ClipboardList
								className="h-7 w-7"
								strokeWidth={2}
							/>
						</div>
						<h2 className="mt-6 text-xl font-bold tracking-tight text-brand-orange/90 md:text-2xl">
							Antes de começar…
						</h2>
						<ol className="mt-6 list-decimal space-y-4 pl-5 text-sm leading-relaxed text-zinc-600">
							<li>
								Responda com sinceridade. Não existem respostas
								certas ou erradas, apenas a sua percepção real.
							</li>
							<li>
								Seus dados são protegidos por criptografia de
								ponta a ponta e tratados de forma anônima pelo
								RH.
							</li>
							<li>
								Ao final, você poderá agendar uma conversa de
								acompanhamento se desejar.
							</li>
						</ol>
						<div className="mt-10">
							<Button
								onClick={handleStartForm}
								text="Iniciar Questionário"
								endIcon={<ArrowRight className="h-5 w-5" />}
								className="w-full justify-center rounded-xl bg-brand-orange/90 px-6 py-3.5 font-semibold text-white hover:bg-brand-orange/80 md:w-auto md:min-w-[240px]"
							/>
						</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="mx-auto flex min-h-full w-full max-w-5xl flex-col px-4 py-8 sm:px-6 sm:py-10 md:px-12 lg:px-16 lg:py-14 xl:max-w-6xl xl:px-20">
			<Helmet>
				<title>{currentForm.title ?? 'Questionário'} | Plumaa</title>
				<meta
					name="description"
					content="Responda às perguntas para que o RH e o acompanhamento possam entender sua situação."
				/>
			</Helmet>

			<div className="mb-6 grid w-full grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 sm:gap-4">
				<p className="text-sm font-semibold tabular-nums text-zinc-800">
					PASSO {currentStep + 1} DE {totalSteps}
				</p>
				<div className="min-w-0 overflow-hidden rounded-full bg-zinc-200">
					<div
						className={`h-2 rounded-full transition-all ease-out ${PROGRESS_COLOR_CLASS}`}
						style={{
							width: `${progressPercent}%`,
							transitionDuration: '300ms',
						}}
					/>
				</div>
				<p className="text-right text-sm font-semibold tabular-nums text-zinc-800">
					{progressPercent}% COMPLETO
				</p>
			</div>

			<div className="flex flex-1 flex-col">
				<div className="mb-2 flex items-center gap-2">
					<span
						className={`inline-flex items-center rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-[0.18em] ${
							currentForm.type === 'ONBOARDING'
								? 'bg-zinc-900 text-zinc-50'
								: 'bg-emerald-50 text-emerald-700'
						}`}
					>
						{currentForm.type === 'ONBOARDING'
							? 'Onboarding Plumaa'
							: 'Formulário da sua empresa'}
					</span>
					{currentForm.title && (
						<span className="truncate text-xs font-medium text-zinc-500">
							{currentForm.title}
						</span>
					)}
				</div>

				<div className="mt-1 flex items-center gap-3">
					<span
						className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-sm font-bold ${questionTextColorClass}`}
						style={{ backgroundColor: resolvedQuestionColor }}
					>
						{questionNumber}
					</span>
					<div className="flex min-w-0 items-center gap-2">
						{currentQuestion.icon_identifier && (
							<DynamicIcon
								name={
									currentQuestion.icon_identifier as IconName
								}
								size={26}
								className="shrink-0 text-zinc-500"
							/>
						)}
						<h1 className="min-w-0 text-2xl font-bold leading-tight text-zinc-900 md:text-3xl">
							{currentQuestion.title}
						</h1>
					</div>
				</div>
				{currentQuestion.description && (
					<p className="mt-3 text-base text-zinc-700">
						{currentQuestion.description}
					</p>
				)}
				{currentQuestion.type !== 'FREE_TEXT' && (
					<div className="mt-3 inline-flex w-fit rounded-full bg-zinc-100 px-2 py-0.5">
						<span className="text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500">
							{currentQuestion.type === 'SINGLE_CHOICE'
								? 'Escolha única'
								: 'Múltipla escolha'}
						</span>
					</div>
				)}

				<div className="mt-8 space-y-4 sm:mt-10 sm:space-y-5 md:mt-12 md:space-y-6">
					{currentQuestion.type === 'FREE_TEXT' && (
						<textarea
							value={
								answers[String(currentQuestion.number)] ?? ''
							}
							onChange={(e) =>
								setAnswer(
									String(currentQuestion.number),
									e.target.value,
								)
							}
							placeholder="Digite sua resposta..."
							rows={4}
							className="w-full rounded-xl border border-zinc-200 bg-white p-4 text-zinc-800 shadow-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 disabled:opacity-60"
						/>
					)}

					{currentQuestion.type === 'SINGLE_CHOICE' &&
						(currentQuestion.options ?? []).map((opt) => {
							const selected =
								(answers[String(currentQuestion.number)] ??
									'') === opt.key;
							const rawOptionColor =
								(opt.color && opt.color.trim()) || '#e5e7eb';
							const resolvedOptionColor =
								resolveColorInputToHex(rawOptionColor) ??
								'#e5e7eb';
							const textColorClass =
								getContrastTextClassForColorInput(
									rawOptionColor,
									'#e5e7eb',
								);
							const borderStyle = selected
								? { borderColor: resolvedOptionColor }
								: undefined;
							const isHovered = hoveredKey === opt.key;
							const hoverBg = isHovered
								? hexToRgba(resolvedOptionColor, 0.04)
								: undefined;
							const selectedBg = selected
								? hexToRgba(resolvedOptionColor, 0.08)
								: undefined;

							return (
								<button
									key={opt.key}
									type="button"
									onClick={() => handleSingleChoice(opt.key)}
									onMouseEnter={() => setHoveredKey(opt.key)}
									onMouseLeave={() => setHoveredKey(null)}
									className="flex w-full items-center gap-3 rounded-xl border-2 border-transparent bg-white p-4 text-left shadow-sm transition-all focus:outline-none disabled:cursor-not-allowed disabled:opacity-60 sm:gap-4 sm:p-5 md:p-6"
									style={{
										...borderStyle,
										...(hoverBg && !selected
											? { backgroundColor: hoverBg }
											: {}),
										...(selectedBg
											? { backgroundColor: selectedBg }
											: {}),
									}}
								>
									<span
										className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-sm font-bold ${textColorClass} sm:h-9 sm:w-9 md:h-10 md:w-10`}
										style={{
											backgroundColor:
												resolvedOptionColor,
										}}
									>
										{selected ? (
											'✓'
										) : opt.icon_identifier ? (
											<DynamicIcon
												name={
													opt.icon_identifier as IconName
												}
												size={18}
												className="text-current"
											/>
										) : (
											opt.key
										)}
									</span>
									<span className="text-sm font-medium text-zinc-800 sm:text-base">
										{opt.label}
									</span>
								</button>
							);
						})}

					{currentQuestion.type === 'MULTIPLE_CHOICE' &&
						(currentQuestion.options ?? []).map((opt) => {
							const current =
								(answers[String(currentQuestion.number)] ?? '')
									.split('')
									.map((s) => s.trim())
									.filter(Boolean) ?? [];
							const selected = current.includes(opt.key);
							const rawOptionColor =
								(opt.color && opt.color.trim()) || '#e5e7eb';
							const resolvedOptionColor =
								resolveColorInputToHex(rawOptionColor) ??
								'#e5e7eb';
							const textColorClass =
								getContrastTextClassForColorInput(
									rawOptionColor,
									'#e5e7eb',
								);
							const borderStyle = selected
								? { borderColor: resolvedOptionColor }
								: undefined;
							const isHovered = hoveredKey === opt.key;
							const hoverBg = isHovered
								? hexToRgba(resolvedOptionColor, 0.04)
								: undefined;
							const selectedBg = selected
								? hexToRgba(resolvedOptionColor, 0.08)
								: undefined;

							return (
								<button
									key={opt.key}
									type="button"
									onClick={() =>
										handleMultipleToggle(opt.key)
									}
									onMouseEnter={() => setHoveredKey(opt.key)}
									onMouseLeave={() => setHoveredKey(null)}
									className="flex w-full items-center gap-3 rounded-xl border-2 border-transparent bg-white p-4 text-left shadow-sm transition-all focus:outline-none disabled:cursor-not-allowed disabled:opacity-60 sm:gap-4 sm:p-5 md:p-6"
									style={{
										...borderStyle,
										...(hoverBg && !selected
											? { backgroundColor: hoverBg }
											: {}),
										...(selectedBg
											? { backgroundColor: selectedBg }
											: {}),
									}}
								>
									<span
										className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-sm font-bold ${textColorClass} sm:h-9 sm:w-9 md:h-10 md:w-10`}
										style={{
											backgroundColor:
												resolvedOptionColor,
										}}
									>
										{selected ? (
											'✓'
										) : opt.icon_identifier ? (
											<DynamicIcon
												name={
													opt.icon_identifier as IconName
												}
												size={18}
												className="text-current"
											/>
										) : (
											opt.key
										)}
									</span>
									<span className="text-sm font-medium text-zinc-800 sm:text-base">
										{opt.label}
									</span>
								</button>
							);
						})}
				</div>

				<div
					ref={nextButtonRef}
					className="mt-8 space-y-3 pb-[calc(env(safe-area-inset-bottom)+0.5rem)] sm:mt-10 sm:space-y-0 sm:pb-0"
				>
					<div className="flex items-center justify-between sm:hidden">
						<div className="min-w-0">
							{currentStep > 0 && (
								<button
									type="button"
									onClick={handleBack}
									className="flex items-center gap-1.5 rounded border-none bg-transparent text-zinc-400 transition-colors hover:text-zinc-600 disabled:cursor-not-allowed disabled:opacity-60"
								>
									<ChevronLeft className="size-5" />
									<span className="text-sm font-semibold uppercase tracking-wide">
										Voltar
									</span>
								</button>
							)}
						</div>
						{/* Pular (mobile): JSX mantido; reativar trocando `false` por `true` e, se voltar o grid 3 colunas, alinhar com o layout antigo. */}
						{false && (
							<button
								type="button"
								onClick={handleSkip}
								disabled={isSubmitting}
								className="rounded-full border border-zinc-300 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-zinc-500 transition-colors hover:border-zinc-400 hover:text-zinc-700 disabled:cursor-not-allowed disabled:opacity-60"
							>
								Pular
							</button>
						)}
					</div>

					<Button
						text={
							isSubmitting
								? 'Enviando...'
								: currentStep < totalSteps - 1
								  ? 'Próximo'
								  : 'Enviar respostas'
						}
						disabled={!canAdvance || isSubmitting}
						style={{
							backgroundColor: resolvedQuestionColor,
							borderColor: resolvedQuestionColor,
						}}
						onClick={() => handleNext()}
						className={`mb-2 w-full justify-center rounded-full px-6 py-2.5 font-bold shadow-md hover:brightness-105 focus:outline-none disabled:opacity-50 sm:hidden ${questionTextColorClass}`}
					/>

					<div className="hidden items-center justify-between gap-4 sm:flex">
						<div className="min-w-0 shrink-0">
							{currentStep > 0 && (
								<button
									type="button"
									onClick={handleBack}
									className="flex items-center gap-1.5 rounded border-none bg-transparent text-zinc-400 transition-colors hover:text-zinc-600 disabled:cursor-not-allowed disabled:opacity-60"
								>
									<ChevronLeft className="size-5" />
									<span className="text-sm font-semibold uppercase tracking-wide">
										Voltar
									</span>
								</button>
							)}
						</div>
						{/* Pular (sm+): JSX mantido; reativar com `true` e ajustar layout (ex.: grid 3 cols). */}
						{false && (
							<button
								type="button"
								onClick={handleSkip}
								disabled={isSubmitting}
								className="shrink-0 rounded-full border border-zinc-300 px-5 py-2 text-sm font-semibold uppercase tracking-wide text-zinc-500 transition-colors hover:border-zinc-400 hover:text-zinc-700 disabled:cursor-not-allowed disabled:opacity-60"
							>
								Pular
							</button>
						)}
						<Button
							text={
								isSubmitting
									? 'Enviando...'
									: currentStep < totalSteps - 1
									  ? 'Próximo'
									  : 'Enviar respostas'
							}
							disabled={!canAdvance || isSubmitting}
							style={{
								backgroundColor: resolvedQuestionColor,
								borderColor: resolvedQuestionColor,
							}}
							onClick={() => handleNext()}
							className={`shrink-0 rounded-full px-6 py-2.5 font-bold shadow-md hover:brightness-105 focus:outline-none disabled:opacity-50 ${questionTextColorClass}`}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

export { UserFormulario };
